import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MeetingDto, MeetingRequestDto } from 'src/api/FiretrolApiClient';
import { meetingApiClient } from 'src/api/FiretrolApiClientInstances';
import { invalidateFor } from 'src/api/queries/invalidationMap';
import { queryKeys } from 'src/api/queries/queryKeys';
import { getBrowserTimeZone } from 'src/utils';

export function useCreateMeeting(jobId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => meetingApiClient.createMeeting(jobId),
    onSettled: () => invalidateFor('meeting.save', qc, { jobId }),
  });
}

export function useCloneMeeting(jobId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (meetingId: string) =>
      meetingApiClient.cloneMeeting(jobId, meetingId),
    onSettled: () => invalidateFor('meeting.save', qc, { jobId }),
  });
}

export function useUpdateMeeting(jobId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      meetingId,
      request,
    }: {
      meetingId: string;
      request: MeetingRequestDto;
    }) => meetingApiClient.updateMeeting(jobId, meetingId, request),
    meta: { successMessage: 'Meeting updated successfully' },
    onSuccess: (meeting: MeetingDto) =>
      qc.setQueryData(queryKeys.meetings.detail(jobId, meeting.id), meeting),
    onSettled: () => invalidateFor('meeting.save', qc, { jobId }),
  });
}

export function useDeleteMeeting(jobId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (meetingId: string) =>
      meetingApiClient.deleteMeeting(jobId, meetingId),
    onSettled: () => invalidateFor('meeting.save', qc, { jobId }),
  });
}

export function usePublishMeeting(jobId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (meetingId: string) =>
      meetingApiClient.publishMeeting(jobId, meetingId, getBrowserTimeZone()),
    meta: { successMessage: 'Meeting Minutes Published' },
    onSettled: (_data, _err, meetingId) =>
      invalidateFor('meeting.publish', qc, { jobId, meetingId }),
  });
}
